/**
 * BUG-2594 / BUG-2595: the POS keeps its own copy of its login.
 *
 * The POS and CW Apps write the same login cookies (accessToken, username, userId, ...)
 * on the parent domain. A CW Apps sign-in as another user in the same browser replaces
 * them, and the POS used to follow whatever user the cookie named: requests, the till
 * list and the bill's user all moved to that user.
 *
 * The record here is written when the POS itself signs in (or refreshes its token) and
 * lives in the POS's own local storage, which CW Apps cannot write. resolveAuthHeaders
 * decides, on every read, which login the POS uses:
 *   - no login cookie            -> signed out, as before (the record is dropped)
 *   - cookie of the same user    -> the cookie, as before (the record follows its token)
 *   - cookie of another user     -> the POS's own record; the cookie is only logged
 * Inside CW Browser the shell owns the login of every partition, so the cookie is always
 * followed there.
 */
import env from "../config/env";
import { trace } from "./debugTrace";

export const POS_SESSION_KEY = "posSession";
export const POS_SESSION_FOREIGN_LOG_KEY = "posSessionForeignLog";
export const POS_SIGNED_OUT_KEY = "posSignedOut";
const FOREIGN_LOG_LIMIT = 20;

const SHARED_COOKIE_NAMES = ["userId", "Enterprise.accessToken", "accessToken", "Enterprise.refreshToken", "Enterprise", "username"];

const normaliseUser = (value) => {
  if (value === null || value === undefined) return "";
  let text = String(value);
  try {
    text = decodeURIComponent(text);
  } catch {
    // keep the raw value
  }
  return text.trim().toLowerCase();
};

export const isSamePosUser = (a, b) => normaliseUser(a) !== "" && normaliseUser(a) === normaliseUser(b);

// The CW Browser shell writes the login cookies of every webview partition itself
// (authBus.setCookiesForPartition) and clears them on its own logout.
export const isShellManagedLogin = () => typeof window !== "undefined" && !!window.electronAPI?.auth;

export const readLoginCookies = () => {
  const cookies = (typeof document !== "undefined" ? document.cookie : "").split("; ");
  const result = { accessToken: undefined, email: undefined, username: undefined, Enterprise: undefined, userId: undefined, refreshToken: undefined };
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].split("=");
    const name = cookie[0];
    const value = cookie[1];
    if (name.includes("accessToken")) result.accessToken = value;
    if (name.includes("LastAuthUser")) result.email = decodeURIComponent(value);
    if (name === "username") result.username = value;
    if (name === "Enterprise") result.Enterprise = value;
    if (name === "userId") result.userId = value;
    if (name === "Enterprise.refreshToken") result.refreshToken = value;
  }
  return result;
};

export const readPosSession = () => {
  try {
    const raw = localStorage.getItem(POS_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    return session && typeof session === "object" && session.accessToken ? session : null;
  } catch {
    return null;
  }
};

const storePosSession = (session) => {
  try {
    localStorage.setItem(POS_SESSION_KEY, JSON.stringify(session));
    localStorage.removeItem(POS_SIGNED_OUT_KEY);
  } catch (err) {
    console.warn("[PosSession] could not store the POS login", err?.message);
  }
};

/** Called by the POS's own sign-in (password, OTP, lock-screen sign-in). */
export const writePosSession = ({ accessToken, refreshToken, username, userId }) => {
  if (!accessToken) return null;
  const session = {
    accessToken,
    refreshToken: refreshToken || null,
    username: username || null,
    userId: userId || null,
    signedInAt: Date.now(),
  };
  storePosSession(session);
  return session;
};

/** Called after the POS refreshes its own token. Keeps the user, replaces the tokens. */
export const updatePosSessionTokens = ({ accessToken, refreshToken }) => {
  const session = readPosSession();
  if (!session || !accessToken) return null;
  const next = { ...session, accessToken, refreshToken: refreshToken || session.refreshToken };
  storePosSession(next);
  return next;
};

export const clearPosSession = () => {
  try {
    localStorage.removeItem(POS_SESSION_KEY);
  } catch {
    // nothing to clear
  }
};

// The user this browser's POS data belongs to, read from storage (the stores cannot be
// imported here: useAuthStore imports getOAuthHeaders).
const readKnownPosUsers = () => {
  const users = [];
  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "null");
    if (userData?.user) users.push(userData.user);
  } catch {
    // fall through to the till owner
  }
  try {
    // userData.user can be the display name ("CW Suite"); these two hold the login name.
    const username = localStorage.getItem("username");
    if (username) users.push(username);
    const owner = localStorage.getItem("tillDataOwner");
    if (owner) users.push(owner);
  } catch {
    // no till owner
  }
  return users;
};

// Set when the POS sees no login cookie (every POS logout clears them); removed by the
// POS's own sign-in. While it is set, a cookie that appears was not written by the POS.
const markPosSignedOut = () => {
  try {
    if (localStorage.getItem(POS_SIGNED_OUT_KEY) !== "1") localStorage.setItem(POS_SIGNED_OUT_KEY, "1");
  } catch {
    // storage not available
  }
};

/** Called by a POS logout: drops the record and notes that the POS signed out here. */
export const endPosSession = () => {
  clearPosSession();
  if (!isShellManagedLogin()) markPosSignedOut();
};

export const isPosSignedOut = () => {
  try {
    return localStorage.getItem(POS_SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
};

let lastForeignLogged = null;

export const readForeignCookieLog = () => {
  try {
    const rows = JSON.parse(localStorage.getItem(POS_SESSION_FOREIGN_LOG_KEY) || "[]");
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
};

// One row per change of the foreign user, not one per request.
const logForeignCookie = (cookieUser, posUser, source) => {
  const key = `${normaliseUser(cookieUser)}|${normaliseUser(posUser)}|${source}`;
  if (lastForeignLogged === key) return;
  lastForeignLogged = key;
  const row = {
    at: new Date().toISOString(),
    cookieUser: cookieUser || null,
    posUser: posUser || null,
    source,
    path: typeof window !== "undefined" ? window.location.pathname : null,
  };
  console.warn("[PosSession] the login cookie names another user; the POS keeps its own login", row);
  trace("PosSession", "foreign login cookie", row);
  try {
    window.electronAPI?.observability?.log("warn", "till-lifecycle", "foreign login cookie", row);
  } catch {
    // diagnostics only
  }
  try {
    const rows = readForeignCookieLog();
    rows.push(row);
    localStorage.setItem(POS_SESSION_FOREIGN_LOG_KEY, JSON.stringify(rows.slice(-FOREIGN_LOG_LIMIT)));
  } catch {
    // diagnostics only
  }
};

const cookieHeaders = (cookie) => ({
  access_token: `bearer ${cookie.accessToken}`,
  email: cookie.email,
  Enterprise: cookie.Enterprise,
  username: cookie.username,
});

/**
 * The login the POS uses right now, in the shape getOAuthHeaders has always returned,
 * or undefined when signed out.
 */
export const resolveAuthHeaders = () => {
  const cookie = readLoginCookies();
  const session = readPosSession();

  // No login cookie: signed out, exactly as before. Every POS logout clears the cookies,
  // and so does the CW Browser shell; the POS's own copy must not outlive them.
  if (!cookie.accessToken) {
    if (session) clearPosSession();
    if (!isShellManagedLogin()) markPosSignedOut();
    return undefined;
  }

  if (isShellManagedLogin()) {
    if (session && cookie.username && session.username && !isSamePosUser(cookie.username, session.username)) {
      logForeignCookie(cookie.username, session.username, "shell");
    }
    return cookieHeaders(cookie);
  }

  if (!session) {
    // A login this record has not seen yet: a build update, or a sign-in that came from
    // the cookie. Keep a copy when the cookie belongs to the user this POS already knows
    // (or knows nobody yet); otherwise behave as before and wait for that sign-in to settle.
    const knownUsers = readKnownPosUsers();
    const cookieIsKnownUser = !cookie.username || knownUsers.length === 0 || knownUsers.some((user) => isSamePosUser(cookie.username, user));
    if (cookieIsKnownUser) {
      let refreshToken = null;
      try {
        refreshToken = localStorage.getItem("refreshToken");
      } catch {
        refreshToken = null;
      }
      writePosSession({
        accessToken: cookie.accessToken,
        refreshToken,
        username: cookie.username || knownUsers[0] || null,
        userId: cookie.userId || null,
      });
      return cookieHeaders(cookie);
    }
    // The POS signed out on this browser (it saw the cookies gone) and has not signed in
    // since, so this cookie was written by another application for a user who is not this
    // till's user. The POS stays signed out and shows its own sign-in form.
    if (isPosSignedOut()) {
      logForeignCookie(cookie.username, knownUsers[0], "signed_out_cookie_ignored");
      return undefined;
    }
    logForeignCookie(cookie.username, knownUsers[0], "no_pos_login");
    return cookieHeaders(cookie);
  }

  // Same user (or a cookie / record that does not name one): the cookie, as before. The
  // record follows the cookie's token so the two never drift apart for one user.
  if (!cookie.username || !session.username || isSamePosUser(cookie.username, session.username)) {
    if (cookie.accessToken !== session.accessToken || (!session.username && cookie.username) || (!session.userId && cookie.userId)) {
      storePosSession({
        ...session,
        accessToken: cookie.accessToken,
        username: session.username || cookie.username || null,
        userId: session.userId || cookie.userId || null,
      });
    }
    return cookieHeaders(cookie);
  }

  // Another user's cookie: the POS stays on its own login.
  logForeignCookie(cookie.username, session.username, "pos_login_kept");
  return {
    access_token: `bearer ${session.accessToken}`,
    email: undefined,
    Enterprise: cookie.Enterprise,
    username: session.username,
  };
};

/** True when the login cookie names a user other than the POS's own login. */
export const hasForeignLoginCookie = (session = readPosSession()) => {
  if (isShellManagedLogin() || !session?.username) return false;
  const cookie = readLoginCookies();
  return !!cookie.accessToken && !!cookie.username && !isSamePosUser(cookie.username, session.username);
};

const cookieExpiry = (accessToken) => {
  try {
    const payload = JSON.parse(atob(accessToken.split(".")[1]));
    return payload?.exp ? "expires=" + new Date(payload.exp * 1000).toUTCString() : "";
  } catch {
    return "";
  }
};

/**
 * Day End runs in CW Apps and takes its user from the shared login cookies. When those
 * name another user, write the POS's own login back before Day End opens, the same six
 * cookies and attributes the POS sign-in writes. Does nothing when the cookies already
 * belong to the POS user or are not there.
 */
export const restoreSharedCookiesForDayEnd = (session = readPosSession()) => {
  if (!session?.accessToken || !hasForeignLoginCookie(session)) return false;
  const foreignUser = readLoginCookies().username;
  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const expires = cookieExpiry(session.accessToken);
  const domainPart = isLocalhost || !env.domain ? "" : "domain=" + env.domain;
  const securePart = isLocalhost ? "" : "secure=true;";
  const values = {
    userId: session.userId,
    "Enterprise.accessToken": session.accessToken,
    accessToken: session.accessToken,
    "Enterprise.refreshToken": session.refreshToken,
    Enterprise: true,
    username: session.username,
  };
  SHARED_COOKIE_NAMES.forEach((name) => {
    if (values[name] === null || values[name] === undefined) return;
    document.cookie = name + "=" + values[name] + ";" + expires + ";path=/;" + securePart + "SameSite=Strict;" + domainPart;
  });
  // The till unlink path clears local storage before Day End opens; a request sent in
  // between would have copied the other user's cookie. Put the POS's own record back too.
  storePosSession(session);
  lastForeignLogged = null;
  logForeignCookie(foreignUser, session.username, "day_end_cookies_restored");
  return true;
};

export const __resetPosSessionLogForTests = () => {
  lastForeignLogged = null;
};
